import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth, getUserDisplayName } from '../firebase';
import '../css/Header.css';
import '../css/stocks.css';
import tickersData from '../resources/tickers-data.json';
import coins from '../resources/coins.json';

function UserIcon() {
  return (
    <svg
      className="topbar-user-icon"
      viewBox="0 0 24 24"
      width="22"
      height="22"
      aria-hidden="true"
      focusable="false"
    >
      <path
        fill="currentColor"
        d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
      />
    </svg>
  );
}

function Header(props) {
  const {
    onLoginClick,
    stockSearchTerm,
    onStockSearchChange,
    onTickerSelect,
    cryptoSearchTerm,
    onCryptoSearchChange,
    onCryptoSelect
  } = props;
  const history = useHistory();
  const location = useLocation();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [userDisplayName, setUserDisplayName] = useState('');
  const searchDropdownRef = useRef(null);
  const userMenuRef = useRef(null);
  const isStocksPage = location.pathname.toLowerCase() === '/stocks';
  const isCryptoPage = location.pathname.toLowerCase() === '/crypto';

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUserDisplayName(user ? getUserDisplayName(user) : '');
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsUserMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.classList.toggle('mobile-menu-open', isMobileMenuOpen);
    return () => document.body.classList.remove('mobile-menu-open');
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchDropdownRef.current && !searchDropdownRef.current.contains(event.target)) {
        setIsSearchOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleProfileClick = () => {
    setIsUserMenuOpen(false);
    history.push('/profile');
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setIsUserMenuOpen(false);
      history.push('/');
    } catch (error) {
      console.error('Logout error:', error.message);
    }
  };

  const filteredTickers = useMemo(() => {
    const term = stockSearchTerm.toLowerCase();
    if (!term) return tickersData;
    return tickersData.filter(
      (ticker) =>
        ticker.ticker.toLowerCase().includes(term) ||
        ticker.name.toLowerCase().includes(term)
    );
  }, [stockSearchTerm]);

  const handleTickerSelect = (ticker) => {
    onTickerSelect(ticker.ticker);
    onStockSearchChange(`${ticker.ticker} - ${ticker.name}`);
    setIsSearchOpen(false);
  };

  const filteredCoins = useMemo(() => {
    const term = cryptoSearchTerm.toLowerCase();
    if (!term) return coins;
    return coins.filter(
      (coin) =>
        coin.ticker.toLowerCase().includes(term) ||
        coin.name.toLowerCase().includes(term)
    );
  }, [cryptoSearchTerm]);

  const handleCryptoSelect = (coin) => {
    onCryptoSelect(coin.ticker);
    onCryptoSearchChange(`${coin.ticker} - ${coin.name}`);
    setIsSearchOpen(false);
  };

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  const handleNavClick = (item) => {
    closeMobileMenu();
    if (item.onClick) item.onClick();
  };

  const menuItems = [
    { to: '/', label: 'News', icon: '📰' },
    { to: '/Stocks', label: 'Stocks', icon: '📈' },
    { to: '/Crypto', label: 'Crypto', icon: '₿' },
    { to: '/About', label: 'About', icon: 'ℹ' },
    ...(!userDisplayName
      ? [{ to: '/login', label: 'Login', icon: '🔐', onClick: onLoginClick }]
      : [])
  ];

  return (
    <>
      <header className="fixed-header topbar">
        <button
          type="button"
          className="burger-menu-btn"
          aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={isMobileMenuOpen}
          onClick={() => setIsMobileMenuOpen((open) => !open)}
        >
          <span className="burger-bar" />
          <span className="burger-bar" />
          <span className="burger-bar" />
        </button>
        <div className="logo">
          <img
            src="./arrow1.png"
            alt="Market Updates"
            style={{ width: '44px', height: '24px' }}
          />
        </div>
        {(isStocksPage || isCryptoPage) && (
          <div className="topbar-search" ref={searchDropdownRef}>
            <input
              type="text"
              className="ticker-search"
              value={isStocksPage ? stockSearchTerm : cryptoSearchTerm}
              onChange={(event) => {
                if (isStocksPage) {
                  onStockSearchChange(event.target.value);
                } else {
                  onCryptoSearchChange(event.target.value);
                }
                setIsSearchOpen(true);
              }}
              onFocus={() => setIsSearchOpen(true)}
              placeholder={isStocksPage ? 'Search for a stock ticker...' : 'Search for a crypto symbol...'}
              aria-label={isStocksPage ? 'Search stock ticker' : 'Search crypto symbol'}
            />
            {isSearchOpen && (
              <div className="ticker-dropdown">
                {isStocksPage &&
                  filteredTickers.slice(0, 15).map((ticker) => (
                    <button
                      key={ticker.ticker}
                      className="ticker-option"
                      onClick={() => handleTickerSelect(ticker)}
                      type="button"
                    >
                      <span className="ticker-symbol">{ticker.ticker}</span>
                      <span className="ticker-name">{ticker.name}</span>
                    </button>
                  ))}
                {isCryptoPage &&
                  filteredCoins.slice(0, 15).map((coin) => (
                    <button
                      key={coin.ticker}
                      className="ticker-option"
                      onClick={() => handleCryptoSelect(coin)}
                      type="button"
                    >
                      <span className="ticker-symbol">{coin.ticker}</span>
                      <span className="ticker-name">{coin.name}</span>
                    </button>
                  ))}
              </div>
            )}
          </div>
        )}
        {userDisplayName && (
          <div className="topbar-user-menu" ref={userMenuRef}>
            <button
              type="button"
              className="topbar-user"
              onClick={() => setIsUserMenuOpen((open) => !open)}
              aria-expanded={isUserMenuOpen}
              aria-haspopup="menu"
              aria-label={`Account menu, signed in as ${userDisplayName}`}
            >
              <UserIcon />
              <span className="topbar-user-name">{userDisplayName}</span>
            </button>
            {isUserMenuOpen && (
              <div className="topbar-user-dropdown" role="menu">
                <button
                  type="button"
                  role="menuitem"
                  className="topbar-user-dropdown-item"
                  onClick={handleProfileClick}
                >
                  Profile Information
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="topbar-user-dropdown-item"
                  onClick={handleLogout}
                >
                  Logout
                </button>
              </div>
            )}
          </div>
        )}
      </header>

      {isMobileMenuOpen && (
        <button
          type="button"
          className="sidebar-backdrop"
          aria-label="Close menu"
          onClick={closeMobileMenu}
        />
      )}

      <aside className={`app-sidebar${isMobileMenuOpen ? ' app-sidebar--open' : ''}`}>
        <nav>
          <ul>
            {menuItems.map((item) => (
              <li key={item.to} className="sidebar-item">
                <Link
                  to={item.to}
                  onClick={() => handleNavClick(item)}
                  className="sidebar-link"
                >
                  <span className="menu-icon" aria-hidden="true">{item.icon}</span>
                  <span className="menu-label">{item.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </aside>
    </>
  );
}

export default Header;
